import axios, { AxiosError } from 'axios';
import { 
  Tenant, 
  User, 
  LoginFormData, 
  LoginResponse, 
  TenantFormData, 
  UserFormData,
  ApiResponse,
  Product,
  ProductFormData,
  LoggerProduct,
  LoggerProductFormData,
  LoggerCustomer,
  LoggerCustomerFormData,
  ConfigFileTemplate
} from '../types';

// Create axios instance with base URL
import { API_URL } from '../utils/ConfigUtils';

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Add request interceptor for JWT token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Check if error is due to token expiration
    if (error.response?.status === 401) {
      // Check error message to see if token expired
      const errorData = error.response.data as any;
      if (errorData?.error?.includes('expired') || errorData?.msg?.includes('expired')) {
        // Clear token and redirect to login
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const login = async (data: LoginFormData): Promise<LoginResponse> => {
  const response = await axiosInstance.post<LoginResponse>('/auth/login', data);
  console.log('Login response:', response.data);
  return response.data;
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await axiosInstance.get<User>('/auth/me');
  return response.data;
};

export const requestForgotPassword = async (usernameOrEmail: string): Promise<{ message: string }> => {
  const trimmed = (usernameOrEmail || '').trim();
  const response = await axiosInstance.post<{ message: string }>(
    '/auth/forgot-password',
    { identifier: trimmed, username: trimmed, email: trimmed },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};

export const resetPasswordWithToken = async (
  token: string,
  password: string,
  confirmPassword: string
): Promise<{ message: string }> => {
  const response = await axiosInstance.post<{ message: string }>('/auth/reset-password', {
    token,
    password,
    confirm_password: confirmPassword,
  });
  return response.data;
};

// Tenants API
export const getTenants = async (params?: { 
  level?: string; 
  parent_id?: string;
  status?: string;
  type?: string;
  group?: string;
  is_deleted?: boolean;
  search?: string;
}): Promise<Tenant[]> => {
  try {
    // Convert the status=deleted param to is_deleted=true when needed
    if (params && params.status === 'deleted') {
      params.is_deleted = true;
      // Delete the status parameter or set it to undefined
      delete params.status;
    }
    const response = await axiosInstance.get<Tenant[]>('/tenants/', { params });
    return response.data;
  } catch (error) {
    // Silently fail to keep UI responsive
    return [];
  }
};

export const getTenant = async (id: string): Promise<Tenant> => {
  try {
    const response = await axiosInstance.get<Tenant>(`/tenants/${id}`);
    return response.data;
  } catch (error) {
    throw error; // Let caller handle
  }
};

export const createTenant = async (data: TenantFormData): Promise<ApiResponse<Tenant>> => {
  try {
    // Remove tenant_code and uuid from the data for creating new tenants
    const { tenant_code, uuid, ...requestData } = data;
    console.log('Creating tenant with data:', requestData);
    console.log('Boolean fields:', {
      attached_ioc: requestData.attached_ioc,
      masking_ioc: requestData.masking_ioc,
      sweeping_on_vision_one: requestData.sweeping_on_vision_one,
      sweeping_on_esdl: requestData.sweeping_on_esdl,
      types: {
        attached_ioc: typeof requestData.attached_ioc,
        masking_ioc: typeof requestData.masking_ioc,
        sweeping_on_vision_one: typeof requestData.sweeping_on_vision_one,
        sweeping_on_esdl: typeof requestData.sweeping_on_esdl
      }
    });
    
    // Log request parameters for debugging
    console.log('API URL:', axiosInstance.defaults.baseURL);
    console.log('Sending API request to create tenant...');
    
    const response = await axiosInstance.post<ApiResponse<Tenant>>('/tenants/', requestData);
    console.log('Create tenant response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating tenant:', error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Error response status:', error.response.status);
      console.error('Error response data:', error.response.data);
      console.error('Error response headers:', error.response.headers);
    } else if (error.request) {
      console.error('No response received, request was:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    console.error('Error config:', error.config);
    
    throw error;
  }
};

export const updateTenant = async (id: string, data: Partial<TenantFormData>): Promise<ApiResponse<Tenant>> => {
  try {
    // Only remove uuid from the data for updating tenants, keep tenant_code
    const { uuid, ...requestData } = data;
    console.log('Updating tenant with data:', requestData);
    console.log('Boolean fields:', {
      attached_ioc: requestData.attached_ioc,
      masking_ioc: requestData.masking_ioc,
      sweeping_on_vision_one: requestData.sweeping_on_vision_one,
      sweeping_on_esdl: requestData.sweeping_on_esdl,
      types: {
        attached_ioc: typeof requestData.attached_ioc,
        masking_ioc: typeof requestData.masking_ioc,
        sweeping_on_vision_one: typeof requestData.sweeping_on_vision_one,
        sweeping_on_esdl: typeof requestData.sweeping_on_esdl
      }
    });
    const response = await axiosInstance.put<ApiResponse<Tenant>>(`/tenants/${id}`, requestData);
    return response.data;
  } catch (error) {
    console.error(`Error updating tenant ${id}:`, error);
    throw error;
  }
};

export const updateTenantStatus = async (id: string, status: string): Promise<ApiResponse<Tenant>> => {
  try {
    // Send the status as-is to the backend - it now handles 'deleted' and 'restore' specially
    const payload = { status };
    
    const response = await axiosInstance.patch<ApiResponse<Tenant>>(`/tenants/${id}/status`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating tenant status:', error);
    throw error;
  }
};

// Users API
export const getUsers = async (params?: { 
  tenant_id?: string; 
  role?: string;
  status?: string;
}): Promise<User[]> => {
  try {
  const response = await axiosInstance.get<User[]>('/users/', { params });
  return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

export const getUser = async (id: string): Promise<User> => {
  try {
  const response = await axiosInstance.get<User>(`/users/${id}`);
  return response.data;
  } catch (error) {
    console.error(`Error fetching user ${id}:`, error);
    throw error;
  }
};

export const createUser = async (data: UserFormData): Promise<ApiResponse<User>> => {
  try {
  const response = await axiosInstance.post<ApiResponse<User>>('/users/', data);
  return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (id: string, data: Partial<UserFormData>): Promise<ApiResponse<User>> => {
  try {
  const response = await axiosInstance.put<ApiResponse<User>>(`/users/${id}`, data);
  return response.data;
  } catch (error) {
    console.error(`Error updating user ${id}:`, error);
    throw error;
  }
};

export const updateUserStatus = async (id: string, status: string): Promise<ApiResponse<User>> => {
  try {
  const response = await axiosInstance.patch<ApiResponse<User>>(`/users/${id}/status`, { status });
  return response.data;
  } catch (error) {
    console.error(`Error updating user ${id} status:`, error);
    throw error;
  }
};

export const resetUserPassword = async (id: string, options?: { force_change?: boolean, admin_override?: boolean }): Promise<{ emailed: boolean }> => {
  try {
    const response = await axiosInstance.post<{ emailed: boolean }>(`/users/${id}/reset-password`, options || { force_change: true });
    return response.data;
  } catch (error) {
    console.error(`Error resetting password for user ${id}:`, error);
    throw error;
  }
};

export const changeOwnPassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post<{ message: string }>('/users/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  } catch (error) {
    console.error('Error changing own password:', error);
    throw error;
  }
};

export const deleteUser = async (id: string): Promise<{ message: string; deleted_user: any }> => {
  try {
    const response = await axiosInstance.delete<{ message: string; deleted_user: any }>(`/users/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting user ${id}:`, error);
    throw error;
  }
};

// Sync regions (cross-region user/product sync)
export const getSyncRegions = async (): Promise<string[]> => {
  try {
    const response = await axiosInstance.get<{ success: boolean; regions: string[] }>('/users/sync-regions');
    if (response.data && response.data.success && Array.isArray(response.data.regions)) {
      return response.data.regions;
    }
    return [];
  } catch (error) {
    console.error('Error fetching sync regions:', error);
    return [];
  }
};

// Products API
export const getProducts = async (params?: { type?: 'saas' | 'logger' }): Promise<Product[]> => {
  try {
    let products: Product[] = [];
    
    // Fetch SaaS products if requested or if fetching all products
    if (!params?.type || params.type === 'saas') {
      const saasResponse = await axiosInstance.get<{success: boolean, products: Product[]}>('/products', { 
        params: { type: 'saas' } 
      });
      products = [...products, ...(saasResponse.data.products || [])];
    }
    
    // Fetch logger products if requested or if fetching all products
    if (!params?.type || params.type === 'logger') {
      const loggerResponse = await axiosInstance.get<any[]>('/logger/products');
      
      // Map logger products to the unified product format
      const loggerProducts: Product[] = loggerResponse.data.map(item => ({
        id: item.id,
        name: item.product_name,
        type: 'logger',
        logo_url: item.logo_url,
        tags: {
          productType: item.product_type,
          logType: item.log_type
        },
        integration_fields: [],
        logger_config: {
          template_name: item.template_name,
          queue_size: item.queue_size,
          max_disk_space: item.max_disk_space,
          worker_threads: item.worker_threads,
          filename: item.filename,
          status: item.status,
          keywords: item.keywords
        },
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
      
      products = [...products, ...loggerProducts];
    }
    
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

export const getProduct = async (id: string): Promise<Product | null> => {
  try {
    // First try the regular products endpoint
    try {
      const response = await axiosInstance.get<{success: boolean, product: Product}>(`/products/${id}`);
      return response.data.product || null;
    } catch (error: any) {
      // If we get a 404, check the logger products endpoint
      if (error.response && error.response.status === 404) {
        console.log(`Product not found in main collection, checking logger products for ID: ${id}`);
        
        // Try to get from logger products collection
        const loggerResponse = await axiosInstance.get<any>(`/logger/products/${id}`);
        
        // If found, convert to unified format
        if (loggerResponse.data) {
          const loggerProduct = loggerResponse.data;
          
          // Convert to unified product format
          const unifiedProduct: Product = {
            id: loggerProduct.id,
            name: loggerProduct.product_name,
            type: 'logger',
            logo_url: loggerProduct.logo_url,
            tags: {
              productType: loggerProduct.product_type,
              logType: loggerProduct.log_type
            },
            integration_fields: [],
            logger_config: {
              template_name: loggerProduct.template_name,
              queue_size: loggerProduct.queue_size,
              max_disk_space: loggerProduct.max_disk_space,
              worker_threads: loggerProduct.worker_threads,
              filename: loggerProduct.filename,
              status: loggerProduct.status,
              keywords: loggerProduct.keywords
            },
            created_at: loggerProduct.created_at,
            updated_at: loggerProduct.updated_at
          };
          
          return unifiedProduct;
        }
      }
      
      // If not found in either collection, return null
      return null;
    }
  } catch (error) {
    console.error(`Error fetching product ${id}:`, error);
    return null;
  }
};

export const createProduct = async (data: ProductFormData): Promise<ApiResponse<Product>> => {
  try {
    // Check if this is a logger product
    if (data.type === 'logger') {
      // For logger products, use the logger_products endpoint
      console.log("Creating logger product using logger API");
      
      // Ensure all keywords have valid conditions (AND or OR)
      const processedKeywords = data.logger_config?.keywords?.map((keyword, index) => {
        // If condition is empty or invalid, set a default
        if (!keyword.condition || (keyword.condition !== 'AND' && keyword.condition !== 'OR')) {
          // Use 'AND' for the first keyword, 'OR' for subsequent ones
          return {
            ...keyword,
            condition: index === 0 ? 'AND' : 'OR'
          };
        }
        return keyword;
      }) || [];
      
      // Prepare logger product data
      const loggerProductData = {
        product_name: data.name,
        product_type: data.tags?.productType || 'default',
        log_type: data.tags?.logType || 'default',
        template_name: data.logger_config?.template_name || 'default_template',
        queue_size: data.logger_config?.queue_size || '1000',
        max_disk_space: data.logger_config?.max_disk_space || '100',
        worker_threads: data.logger_config?.worker_threads || '4',
        filename: data.logger_config?.filename || 'default.log',
        status: data.logger_config?.status || 'active',
        keywords: processedKeywords
      };
      
      console.log("API createLoggerProduct - Sending logger data to API:", JSON.stringify(loggerProductData, null, 2));
      
      const response = await axiosInstance.post<{success: boolean, message: string, product: any}>('/logger/products', loggerProductData);
      
      console.log("API createLoggerProduct - Response data:", response.data);
      
      // Convert logger product to unified product format for frontend
      const unifiedProduct: Product = {
        id: response.data.product.id,
        name: response.data.product.product_name,
        type: 'logger',
        logo_url: response.data.product.logo_url,
        tags: {
          productType: response.data.product.product_type,
          logType: response.data.product.log_type
        },
        integration_fields: [],
        logger_config: {
          template_name: response.data.product.template_name,
          queue_size: response.data.product.queue_size,
          max_disk_space: response.data.product.max_disk_space,
          worker_threads: response.data.product.worker_threads,
          filename: response.data.product.filename,
          status: response.data.product.status,
          keywords: response.data.product.keywords
        },
        created_at: response.data.product.created_at,
        updated_at: response.data.product.updated_at
      };
      
      return {
        success: true,
        data: unifiedProduct,
        message: response.data.message || 'Logger product created successfully'
      };
    }
    
    // For SaaS products, use the regular products endpoint
    console.log("Creating SaaS product using regular API");
    
    // Ensure integration_fields is properly formatted
    const formattedData = {
      ...data,
      type: 'saas', // Explicitly set type for SaaS products
      integration_fields: data.integration_fields?.map(field => ({
        field_key: field.field_key,
        label: field.label,
        type: field.type,
        is_required: field.is_required,
        ...(field.default_value ? { default_value: field.default_value } : {}),
        ...(field.type === 'multi-select' && field.options ? { options: field.options } : {}),
        ...(field.type === 'soar-mapping' && field.field_mapping ? { field_mapping: field.field_mapping } : {})
      })) || []
    };
    
    console.log("API createProduct - Sending product data to API:", JSON.stringify(formattedData, null, 2));
    console.log("API createProduct - Integration fields count:", formattedData.integration_fields?.length || 0);
    
    const response = await axiosInstance.post<{success: boolean, message: string, product: Product}>('/products', formattedData);
    
    console.log("API createProduct - Response data:", response.data);
    console.log("API createProduct - Returned product:", response.data.product);
    console.log("API createProduct - Returned integration fields:", 
      response.data.product.integration_fields?.length || 0);
    
    return {
      success: true,
      data: response.data.product,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Error creating product:', error);
    // Enhanced error logging
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    throw error;
  }
};

export const updateProduct = async (id: string, data: Partial<ProductFormData>): Promise<ApiResponse<Product>> => {
  try {
    // Check if this is a logger product
    if (data.type === 'logger') {
      // For logger products, prepare data for logger endpoint
      const loggerProductData = {
        product_name: data.name,
        product_type: data.tags?.productType || '',
        log_type: data.tags?.logType || '',
        logo_url: data.logo_url || '',
        template_name: data.logger_config?.template_name || '',
        queue_size: data.logger_config?.queue_size || '',
        max_disk_space: data.logger_config?.max_disk_space || '',
        worker_threads: data.logger_config?.worker_threads || '',
        filename: data.logger_config?.filename || '',
        status: data.logger_config?.status || '',
        keywords: data.logger_config?.keywords || []
      };
      
      // Update using the logger products endpoint
      const response = await axiosInstance.put<{success: boolean, message: string, data: any}>(`/logger/products/${id}`, loggerProductData);
      
      // Convert to unified product format
      const unifiedProduct: Product = {
        id: response.data.data.id,
        name: response.data.data.product_name,
        type: 'logger',
        logo_url: response.data.data.logo_url,
        tags: {
          productType: response.data.data.product_type,
          logType: response.data.data.log_type
        },
        integration_fields: [],
        logger_config: {
          template_name: response.data.data.template_name,
          queue_size: response.data.data.queue_size,
          max_disk_space: response.data.data.max_disk_space,
          worker_threads: response.data.data.worker_threads,
          filename: response.data.data.filename,
          status: response.data.data.status,
          keywords: response.data.data.keywords
        },
        created_at: response.data.data.created_at,
        updated_at: response.data.data.updated_at
      };
      
      return {
        success: true,
        data: unifiedProduct,
        message: response.data.message
      };
    }
    
    // For SaaS products, use the regular products endpoint
    // Ensure integration_fields is properly formatted
    const formattedData = {
      ...data,
      integration_fields: data.integration_fields?.map(field => ({
        field_key: field.field_key,
        label: field.label,
        type: field.type,
        is_required: field.is_required,
        ...(field.default_value ? { default_value: field.default_value } : {}),
        ...(field.type === 'multi-select' && field.options ? { options: field.options } : {}),
        ...(field.type === 'soar-mapping' && field.field_mapping ? { field_mapping: field.field_mapping } : {})
      })) || []
    };
    
    console.log("API updateProduct - Updating product ID:", id);
    console.log("API updateProduct - Update data:", JSON.stringify(formattedData, null, 2));
    console.log("API updateProduct - Integration fields count:", formattedData.integration_fields?.length || 0);
    
    const response = await axiosInstance.put<{success: boolean, message: string, product: Product}>(`/products/${id}`, formattedData);
    
    console.log("API updateProduct - Response data:", response.data);
    console.log("API updateProduct - Updated product:", response.data.product);
    console.log("API updateProduct - Updated integration fields:", 
      response.data.product.integration_fields?.length || 0);
    
    return {
      success: true,
      data: response.data.product,
      message: response.data.message
    };
  } catch (error) {
    console.error(`Error updating product ${id}:`, error);
    throw error;
  }
};

export const deleteProduct = async (id: string): Promise<ApiResponse<null>> => {
  try {
    // First check which collection the product is in
    const product = await getProduct(id);
    
    if (!product) {
      throw new Error(`Product with ID ${id} not found`);
    }
    
    let response;
    
    // If it's a logger product, use the logger products endpoint
    if (product.type === 'logger') {
      console.log(`API deleteProduct - Deleting logger product with ID: ${id}`);
      response = await axiosInstance.delete<{success: boolean, message: string}>(`/logger/products/${id}`);
    } else {
      // Otherwise use the regular products endpoint
      console.log(`API deleteProduct - Deleting product with ID: ${id}`);
      response = await axiosInstance.delete<{success: boolean, message: string}>(`/products/${id}`);
    }
    
    console.log("API deleteProduct - Response:", response.data);
    
    return {
      success: true,
      message: response.data.message
    };
  } catch (error: any) {
    console.error(`Error deleting product ${id}:`, error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

// Product Integrations API
export const getProductIntegration = async (customerId: string, productId: string): Promise<any> => {
  try {
    console.log(`Attempting to fetch integration: customer=${customerId}, product=${productId}`);
    const response = await axiosInstance.get<{success: boolean, integration: any}>(`/product-integrations/customer/${customerId}/product/${productId}`);
    console.log(`Integration found:`, response.data.integration);
    return response.data.integration || null;
  } catch (error: any) {
    // Only log detailed errors for non-404 responses
    if (error.response && error.response.status !== 404) {
      console.error(`Error fetching product integration:`, error);
    } else if (error.response && error.response.status === 404) {
      // For 404, just log a simple message that integration doesn't exist
      console.log(`No integration found for customer ${customerId} and product ${productId} - This is normal for new integrations`);
      // Return null to indicate no integration exists
      return null;
    } else {
      // For other errors, log more details
      console.error('Network or other error fetching integration:', error);
    }
    return null;
  }
};

// Define a type for integration batch response
interface IntegrationBatchResponse {
  [key: string]: any; // Allow any string keys 
  active_status?: {
    [productId: string]: {
      [orgId: string]: boolean;
    };
  };
}

// New method to get multiple integrations status efficiently using the batch endpoint
export const getMultipleIntegrationsStatus = async (
  customerIds: string[], 
  productIds: string[]
): Promise<IntegrationBatchResponse> => {
  try {
    // Use optimized v2 endpoint that accepts arrays of IDs
    const response = await axiosInstance.post<{success: boolean, integrations: IntegrationBatchResponse}>(
      '/product-integrations/batch-check/v2',
      { customer_ids: customerIds, product_ids: productIds }
    );
    
    return response.data.integrations || {};
  } catch (error) {
    console.error('Error fetching multiple integration statuses:', error);
    
    // Fall back to the old implementation if the batch endpoint fails
    const result: IntegrationBatchResponse = {};
    
    // Initialize each product's status object
    productIds.forEach(productId => {
      result[productId] = {};
      
      // Initialize each customer's status for this product to false
      customerIds.forEach(customerId => {
        result[productId][customerId] = false;
      });
    });
    
    // No v2 available: perform a lightweight fallback without flooding the API
    // We will not issue N+1 detailed calls; just return initialized false map
    
    return result;
  }
};

export const createProductIntegration = async (data: {
  customer_id: string,
  product_id: string,
  values: Record<string, any>
}): Promise<ApiResponse<any>> => {
  try {
    console.log(`Creating product integration: customer=${data.customer_id}, product=${data.product_id}`, data);
    
    // Validate that we have all required fields
    if (!data.customer_id || !data.product_id) {
      console.error('Missing required fields for integration creation');
      return {
        success: false,
        message: 'Missing required customer_id or product_id',
        data: null
      };
    }
    
    // Make the API request
    const response = await axiosInstance.post<{success: boolean, message: string, integration: any}>('/product-integrations', data);
    
    console.log(`Integration created successfully:`, response.data);
    return {
      success: true,
      data: response.data.integration,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Error creating product integration:', error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Error response status:', error.response.status);
      console.error('Error response data:', error.response.data);
      
      // Return a meaningful error message based on status code
      if (error.response.status === 404) {
        return {
          success: false,
          message: 'Integration endpoint not found. Please check the API routes.',
          data: null
        };
      } else if (error.response.status === 400) {
        return {
          success: false,
          message: error.response.data?.error || 'Invalid integration data',
          data: null
        };
      } else {
        return {
          success: false,
          message: error.response.data?.error || 'Failed to create integration',
          data: null
        };
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
      return {
        success: false,
        message: 'No response received from server. Please check your network connection.',
        data: null
      };
    } else {
      console.error('Error message:', error.message);
      return {
        success: false,
        message: error.message || 'Unknown error occurred',
        data: null
      };
    }
  }
};

// Update product integration activation status
export const updateProductIntegrationStatus = async (data: {
  customer_id: string,
  product_id: string,
  is_active: boolean
}): Promise<ApiResponse<any>> => {
  try {
    // Use the exact route from the backend: '/product-integrations/status' with PATCH
    const response = await axiosInstance.patch<{success: boolean, message: string, integration: any}>(
      '/product-integrations/status', 
      data  // Send the complete data object as is
    );
    
    return {
      success: true,
      data: response.data.integration,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Error updating product integration status:', error);
    
    // Enhanced error logging
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

// Get tenant for integration purposes (bypassing tenant access control)
export const getTenantForIntegration = async (id: string): Promise<any> => {
  try {
    console.log(`Getting tenant for integration: ${id}`);
    const response = await axiosInstance.get<{success: boolean, tenant: any}>(`/integration-tenants/${id}`);
    return response.data.tenant;
  } catch (error) {
    console.error(`Error fetching tenant for integration ${id}:`, error);
    throw error;
  }
};

// Get all organizations for integration purposes
export const getOrganizationsForIntegration = async (): Promise<Tenant[]> => {
  try {
    // Use the regular getTenants function with a filter for active organizations
    const tenants = await getTenants({ 
      status: 'active',
      // We're not filtering by level anymore to include L0, L2, and L3
    });
    
    // Sort organizations by level (L0, L2, L3) and then by name
    return tenants.sort((a, b) => {
      // First sort by level
      const levelOrder: Record<string, number> = { 'L0': 0, 'L2': 1, 'L3': 2 };
      const levelA = levelOrder[a.level] || 999;
      const levelB = levelOrder[b.level] || 999;
      
      if (levelA !== levelB) {
        return levelA - levelB;
      }
      
      // Then sort by name
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    console.error('Error fetching customers for integration:', error);
    return [];
  }
};

// Get integration values for a specific product and customer
export const getIntegrationValues = async (productId: string, customerId: string): Promise<any> => {
  try {
    console.log(`Fetching integration values for product ${productId} and customer ${customerId}`);
    
    // Validate inputs
    if (!productId || !customerId) {
      console.error('Missing required parameters', { productId, customerId });
      throw new Error('Product ID and Customer ID are required');
    }
    
    const response = await axiosInstance.get<{success: boolean, integration: any}>(
      `/product-integrations/customer/${customerId}/product/${productId}`
    );
    
    if (response.data.success && response.data.integration) {
      console.log(`Successfully retrieved integration values`, response.data.integration);
      return response.data.integration;
    }
    
    console.warn('Integration found but data is empty or invalid', response.data);
    return null;
  } catch (error: any) {
    // Handle specific error cases
    if (error.response) {
      if (error.response.status === 404) {
        // For 404, just log a simple message that integration doesn't exist
        console.log(`No integration found for customer ${customerId} and product ${productId} - This is normal for new integrations`);
      } else {
        // For other error statuses, log more details
        console.error(`Error fetching integration values (status ${error.response.status}):`, 
          error.response.data?.error || error.response.statusText);
      }
    } else {
      // For network or other errors
      console.error(`Network or other error fetching integration:`, error.message || error);
    }
    
    // Rethrow so the caller can handle appropriately
    throw error;
  }
};

// Remove region from tenant filter parameters
export interface TenantFilterParams {
  level?: string;
  parent_id?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Add a function to delete product integration
export const deleteProductIntegration = async (params: { 
  customer_id: string;
  product_id: string;
}) => {
  try {
    const response = await axiosInstance.delete(
      `/product-integrations/customer/${params.customer_id}/product/${params.product_id}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting product integration:', error);
    throw error;
  }
};

// Get user by ID (cached lookup for displaying user names)
export const getUserNameById = async (userId: string): Promise<string> => {
  try {
    // If the ID looks like a MongoDB ObjectID (24 characters, hex)
    if (/^[0-9a-f]{24}$/i.test(userId)) {
      console.log(`Fetching user name for ID: ${userId}`);
      
      // Try multiple API endpoint patterns to find the one that works
      let userData = null;
      
      try {
        // Try the direct ID endpoint first
        const response = await axiosInstance.get<any>(`/users/${userId}`);
        console.log(`Direct endpoint response:`, response.data);
        userData = response.data.user || response.data;
      } catch (directError) {
        console.log(`Direct endpoint failed, trying with param:`, directError);
        
        // If that fails, try with a query parameter
        try {
          const paramResponse = await axiosInstance.get<any>(`/users`, { params: { id: userId } });
          console.log(`Param endpoint response:`, paramResponse.data);
          
          // If the response is an array, find the matching user
          if (Array.isArray(paramResponse.data)) {
            userData = paramResponse.data.find(user => 
              user._id === userId || 
              user.id === userId || 
              (user._id && user._id.$oid === userId)
            );
          } else {
            userData = paramResponse.data.user || paramResponse.data;
          }
        } catch (paramError) {
          console.log(`Param endpoint also failed:`, paramError);
        }
      }
      
      if (userData) {
        console.log(`Found user data:`, userData);
        
        // Check for first_name and last_name fields
        if (userData.first_name && userData.last_name) {
          return `${userData.first_name} ${userData.last_name}`;
        } 
        // Check for name field
        else if (userData.name) {
          return userData.name;
        }
        // Check for username field
        else if (userData.username) {
          return userData.username;
        }
        // Check for email field
        else if (userData.email) {
          return userData.email;
        }
        
        console.log(`Available user fields:`, Object.keys(userData));
      } else {
        console.log(`No user data found for ID ${userId}`);
      }
    }
    
    // If not a valid ID or user not found, return formatted ID
    return userId.startsWith('User ') ? userId : `User ${userId.substring(0, 5)}`;
  } catch (error) {
    console.log(`Could not fetch user details for ID: ${userId}`, error);
    // Return a formatted version of the ID if the API call fails
    return userId.startsWith('User ') ? userId : `User ${userId.substring(0, 5)}`;
  }
};

// Logger Product API
export const getLoggerProducts = async (): Promise<LoggerProduct[]> => {
  try {
    const response = await axiosInstance.get<LoggerProduct[]>('/logger/products');
    return response.data;
  } catch (error) {
    console.error('Error fetching logger products:', error);
    return [];
  }
};

export const getLoggerProduct = async (id: string): Promise<LoggerProduct | null> => {
  try {
    const response = await axiosInstance.get<LoggerProduct>(`/logger/products/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching logger product ${id}:`, error);
    return null;
  }
};

export const createLoggerProduct = async (data: LoggerProductFormData): Promise<ApiResponse<LoggerProduct>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<LoggerProduct>>('/logger/products', data);
    return response.data;
  } catch (error) {
    console.error('Error creating logger product:', error);
    throw error;
  }
};

export const updateLoggerProduct = async (id: string, data: Partial<LoggerProductFormData>): Promise<ApiResponse<LoggerProduct>> => {
  try {
    // Ensure all keywords have valid conditions (AND or OR)
    const processedKeywords = data.keywords?.map((keyword, index) => {
      // If condition is empty or invalid, set a default
      if (!keyword.condition || (keyword.condition !== 'AND' && keyword.condition !== 'OR')) {
        // Use 'AND' for the first keyword, 'OR' for subsequent ones
        return {
          ...keyword,
          condition: index === 0 ? 'AND' : 'OR'
        };
      }
      return keyword;
    }) || data.keywords;
    
    // Create a new object with processed keywords
    const processedData = {
      ...data,
      keywords: processedKeywords
    };
    
    const response = await axiosInstance.put<ApiResponse<LoggerProduct>>(`/logger/products/${id}`, processedData);
    return response.data;
  } catch (error) {
    console.error(`Error updating logger product ${id}:`, error);
    throw error;
  }
};

export const deleteLoggerProduct = async (id: string): Promise<ApiResponse<null>> => {
  try {
    const response = await axiosInstance.delete<ApiResponse<null>>(`/logger/products/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting logger product ${id}:`, error);
    throw error;
  }
};

// Logger Customer APIs
export const getLoggerCustomers = async (): Promise<LoggerCustomer[]> => {
  try {
    const response = await axiosInstance.get<LoggerCustomer[]>('/logger/customers');
    return response.data;
  } catch (error) {
    console.error('Error fetching logger customers:', error);
    return [];
  }
};

export const getLoggerCustomer = async (id: string): Promise<LoggerCustomer | null> => {
  try {
    // Use the recommended endpoint with customer_id parameter
    const response = await axiosInstance.get<any[]>('/logger/integrations', {
      params: { customer_id: id }
    });
    
    // If no integrations found, return null
    if (!response.data || response.data.length === 0) {
      return null;
    }
    
    // Get all product IDs from integrations
    const productIds = response.data.map(integration => integration.product_id);
    
    // Get settings from first integration
    const firstIntegration = response.data[0];
    
    // Create a map of product-specific data
    const productDataMap: Record<string, any> = {};
    response.data.forEach(integration => {
      productDataMap[integration.product_id] = {
        keywords: integration.keywords || [],
        device_ids: integration.device_ids || []
      };
    });
    
    // Construct customer object in the expected format using type assertion
    const customer = {
      id: firstIntegration.id,
      customer_id: id,
      target: firstIntegration.target || "listener01.ae.eventussecurity.com",
      status: firstIntegration.is_active ? "active" : "inactive",
      logger_products: productIds,
      logger_type: firstIntegration.logger_type || "logger01",
      logger_config: firstIntegration.logger_config || {
        product_type: '',
        log_type: '',
        template_name: '',
        queue_size: 1000,
        max_disk_space: 100,
        worker_threads: 4,
        filename: ''
      },
      tls_config: firstIntegration.tls_config || {},
      keywords: firstIntegration.keywords || [],
      device_ids: firstIntegration.device_ids || [],
      product_data: productDataMap, // Add product-specific data
      created_at: firstIntegration.created_at,
      updated_at: firstIntegration.updated_at
    } as LoggerCustomer; // Use type assertion
    
    return customer;
  } catch (error) {
    console.error(`Error fetching logger customer ${id}:`, error);
    return null;
  }
};

export const createLoggerCustomer = async (data: LoggerCustomerFormData): Promise<ApiResponse<LoggerCustomer>> => {
  try {
    // Ensure all required fields are present and properly formatted
    const requestData = {
      customer_id: data.customer_id,
      target: data.target || "listener01.ae.eventussecurity.com", // Provide default if missing
      logger_type: data.logger_type || "Logger 01", // Provide default if missing
      status: data.status || "active", // Provide default if missing
      logger_products: Array.isArray(data.logger_products) ? data.logger_products : [],
      // Format logger_config properly
      logger_config: {
        product_type: (data as any).logger_config?.product_type || "logger",
        log_type: (data as any).logger_config?.log_type || "syslog",
        template_name: (data as any).logger_config?.template_name || "default_template",
        filename: (data as any).logger_config?.filename || "%HOSTNAME%.log",
        queue_size: parseInt((data as any).logger_config?.queue_size) || 1000,
        max_disk_space: parseInt((data as any).logger_config?.max_disk_space) || 100,
        worker_threads: parseInt((data as any).logger_config?.worker_threads) || 4
      },
      // Format tls_config properly
      tls_config: {
        cacert: (data as any).tls_config?.cacert || "",
        mycert: (data as any).tls_config?.mycert || "",
        myprivkey: (data as any).tls_config?.myprivkey || "",
        permittedpeer: (data as any).tls_config?.permittedpeer || ""
      }
    };
    
    // Validate required fields
    if (!requestData.customer_id) {
      throw new Error("Customer ID is required");
    }
    
    if (requestData.logger_products.length === 0) {
      throw new Error("At least one logger product must be selected");
    }
    
    console.log('Creating logger customer with data:', JSON.stringify(requestData, null, 2));
    
    const response = await axiosInstance.post<ApiResponse<LoggerCustomer>>('/logger/customers', requestData);
    return response.data;
  } catch (error: any) {
    console.error('Error creating logger customer:', error);
    
    // Provide more detailed error information
    if (error.response && error.response.data) {
      console.error('Server error response:', error.response.data);
      
      // Extract error message from response if available
      const errorMessage = error.response.data.message || 
                          error.response.data.error || 
                          `Failed with status ${error.response.status}`;
      
      throw new Error(errorMessage);
    }
    
    throw error;
  }
};

export const updateLoggerCustomer = async (id: string, data: LoggerCustomerFormData): Promise<ApiResponse<LoggerCustomer>> => {
  try {
    // Use type assertion to bypass TypeScript checks
    const requestData = {
      customer_id: data.customer_id,
      logger_products: data.logger_products,
      status: data.status,
      logger_config: (data as any).logger_config,
      tls_config: (data as any).tls_config
    };
    
    console.log('Updating logger customer with data:', requestData);
    
    try {
      const response = await axiosInstance.put<ApiResponse<LoggerCustomer>>(`/logger/customers/${id}`, requestData);
      return response.data;
    } catch (error: any) {
      // If the customer is gone (410), create a new one
      if (error.response && error.response.status === 410) {
        console.log('Customer not found (410 Gone), creating a new one instead');
        return createLoggerCustomer(data);
      }
      throw error;
    }
  } catch (error) {
    console.error(`Error updating logger customer ${id}:`, error);
    throw error;
  }
};

export const deleteLoggerCustomer = async (id: string): Promise<ApiResponse<null>> => {
  try {
    const response = await axiosInstance.delete<ApiResponse<null>>(`/logger/customers/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting logger customer ${id}:`, error);
    throw error;
  }
};

export const generateConfigFile = async (customerId: string, loggerType?: string, products?: string): Promise<any> => {
  try {
    // Remove the duplicate /api/ prefix
    let url = `/logger/config?customer_id=${customerId}`;
    
    if (loggerType) {
      url += `&logger_type=${loggerType}`;
    }
    
    if (products) {
      url += `&products=${products}`;
    }
    
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error('Error generating config file:', error);
    throw error;
  }
};

// Update logger customer status (for product activation/deactivation)
export const updateLoggerCustomerStatus = async (data: any) => {
  try {
    // Remove the duplicate /api/ prefix
    const response = await axiosInstance.post('/logger/customers/status', data);
    return response.data;
  } catch (error) {
    console.error('Error updating logger customer status:', error);
    throw error;
  }
};

// Update logger integration settings (target, TLS, etc.)
export const updateLoggerIntegrationSettings = async (data: {
  customer_id: string;
  target?: string;
  logger_type?: string;
  logger_products?: string[];
  keywords?: Array<{keyword: string, condition: string}>;
  tls_cacert?: string;
  tls_mycert?: string;
  tls_myprivkey?: string;
  tls_permittedpeer?: string;
  enable_cyberark?: boolean;
  additional_conditions?: string;
}): Promise<ApiResponse<any>> => {
  try {
    // Use the axiosInstance with the correct URL prefix
    const response = await axiosInstance.post<ApiResponse<any>>('/logger/integrations/settings', data);
    return response.data;
  } catch (error) {
    console.error('Error updating logger integration settings:', error);
    throw error;
  }
};

// Get logger product integrations
export const getLoggerIntegrations = async (params?: {
  customer_id?: string;
  product_id?: string;
}): Promise<any[]> => {
  try {
    const response = await axiosInstance.get('/logger/integrations', { params });
    return response.data || [];
  } catch (error) {
    console.error('Error fetching logger integrations:', error);
    return [];
  }
};

// Get logger integration batch status
export const getLoggerIntegrationsBatch = async (
  customerIds: string[],
  productIds: string[]
): Promise<Record<string, Record<string, boolean>>> => {
  try {
    // Prepare the combinations array
    const combinations = [];
    for (const productId of productIds) {
      for (const customerId of customerIds) {
        combinations.push({ customer_id: customerId, product_id: productId });
      }
    }
    
    // Call the batch endpoint
    const response = await axiosInstance.post<{success: boolean, integrations: Record<string, Record<string, boolean>>}>(
      '/logger/integrations/batch',
      { combinations }
    );
    
    return response.data.integrations || {};
  } catch (error) {
    console.error('Error fetching logger integration batch status:', error);
    
    // Return empty result in case of error
    const result: Record<string, Record<string, boolean>> = {};
    productIds.forEach(productId => {
      result[productId] = {};
      customerIds.forEach(customerId => {
        result[productId][customerId] = false;
      });
    });
    
    return result;
  }
};

// Forwarding config
export type ForwardingCluster = {
  id: string;
  name: string;
  destination: string;
  port: string;
  tenantIds: string[];
};

export const getForwardingConfig = async (): Promise<{ clusters: ForwardingCluster[] }> => {
  const res = await axiosInstance.get('/forwarding/config');
  return res.data || { clusters: [] };
};

export const saveForwardingConfig = async (payload: { clusters: ForwardingCluster[] }) => {
  const res = await axiosInstance.post('/forwarding/config', payload);
  return res.data;
};

// Get product integrations by product ID
export const getProductIntegrations = async (productId: string): Promise<any[]> => {
  try {
    if (!productId) {
      console.error('Product ID is required');
      return [];
    }
    
    console.log(`Fetching integrations for product ID: ${productId}`);
    
    // Use the new endpoint that returns all integrations for a product (both SaaS and logger)
    try {
      const response = await axiosInstance.get<{success: boolean, integrations: any[]}>(
        `/product-integrations/product/${productId}`
      );
      
      if (response.data.success && response.data.integrations) {
        console.log(`Found ${response.data.integrations.length} integrations via direct endpoint`);
        return response.data.integrations;
      }
    } catch (error: any) {
      console.log('Direct endpoint failed, falling back to alternative approach:', error);
    }
    
    // Fallback: Use the alternative approach
    console.log('Using alternative approach for product integrations');
    
    // Get all organizations (customers) that could potentially be integrated
    const organizations = await getOrganizationsForIntegration();
    console.log(`Checking ${organizations.length} customers for integrations`);
    
    // To avoid too many requests, we'll use the batch check endpoint if available
    try {
      // Extract organization IDs
      const orgIds = organizations.map(org => (org as any).id || (org as any)._id).filter(Boolean);
      
      if (orgIds.length > 0) {
        // Use the batch check endpoint to efficiently check multiple integrations
        const batchResult = await getMultipleIntegrationsStatus(orgIds, [productId]);
        
        // Extract the organizations that have integrations
        const integratedOrgIds = [];
        if (batchResult[productId]) {
          for (const [orgId, isIntegrated] of Object.entries(batchResult[productId])) {
            if (isIntegrated) {
              integratedOrgIds.push(orgId);
            }
          }
        }
        
        console.log(`Found ${integratedOrgIds.length} integrations via batch check`);
        
        // If we found integrations, get the full details for each one
        if (integratedOrgIds.length > 0) {
          const integrations: any[] = [];
          
          for (const orgId of integratedOrgIds) {
            try {
              // Find the organization object
              const org = organizations.find(o => (o as any).id === orgId || (o as any)._id === orgId);
              
              if (org) {
                // Get full integration details
                const integration = await getProductIntegration(orgId, productId);
                
                if (integration) {
                  integrations.push({
                    customer_id: orgId,
                    product_id: productId,
                    customer: org,
                    integration
                  });
                }
              }
            } catch (err) {
              // Continue checking other organizations even if one fails
              console.log(`Error getting integration details for org ${orgId}:`, err);
            }
          }
          
          return integrations;
        }
      }
    } catch (batchError) {
      console.log('Batch check failed, falling back to individual checks:', batchError);
    }
    
    // Fallback: Check each organization individually
    // Limit to a reasonable number to avoid too many requests
    const maxOrgsToCheck = 10;
    const orgsToCheck = organizations.slice(0, maxOrgsToCheck);
    console.log(`Falling back to checking ${orgsToCheck.length} customers individually`);
    
    const integrations: any[] = [];
    const checkPromises = orgsToCheck.map(async (org) => {
      try {
        const orgId = (org as any).id || (org as any)._id;
        if (orgId) {
          const integration = await getProductIntegration(orgId, productId);
          if (integration) {
            integrations.push({
              customer_id: orgId,
              product_id: productId,
              customer: org,
              integration
            });
          }
        }
      } catch (error) {
        // Skip errors and continue checking other organizations
      }
    });
    
    // Wait for all checks to complete
    await Promise.all(checkPromises);
    console.log(`Found ${integrations.length} integrations via individual checks`);
    
    return integrations;
  } catch (error) {
    console.error(`Error fetching product integrations for product ${productId}:`, error);
    return [];
  }
};
// Audit Log API
export interface AuditLogFilters {
  event_type?: string;
  user_id?: string;
  username?: string;
  endpoint?: string;
  method?: string;
  status_code?: number;
  tenant_id?: string;
  ip_address?: string;
  start_date?: string;
  end_date?: string;
  time_range?: 'last_24h' | 'last_7d' | 'last_30d' | 'custom';
  page?: number;
  per_page?: number;
  include_data?: boolean;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  event_type: string;
  user_id?: string;
  username?: string;
  ip_address?: string;
  endpoint?: string;
  method?: string;
  action?: string;
  activity_description?: string;
  status_code?: number;
  response_time?: number;
  request_data?: any;
  response_data?: any;
  error_message?: string;
  tenant_id?: string;
  user_agent?: string;
  referrer?: string;
  session_id?: string;
}

export interface AuditLogPagination {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

export interface AuditLogResponse {
  logs: AuditLogEntry[];
  pagination: AuditLogPagination;
}

export interface AuditLogStats {
  total_logs: number;
  time_range: string;
  start_time: string;
  end_time: string;
  event_types: Array<{type: string, count: number}>;
  status_codes: Array<{code: number, count: number}>;
  top_endpoints: Array<{endpoint: string, count: number}>;
  top_users: Array<{username: string, count: number}>;
  error_count: number;
  average_response_time: number;
}

export const getAuditLogs = async (filters: AuditLogFilters = {}): Promise<AuditLogResponse> => {
  try {
    const response = await axiosInstance.get<AuditLogResponse>('/audit-logs/', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    throw error;
  }
};

export const getAuditLog = async (id: string): Promise<AuditLogEntry> => {
  try {
    const response = await axiosInstance.get<AuditLogEntry>(`/audit-logs/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching audit log:', error);
    throw error;
  }
};

export const getAuditLogStats = async (timeRange: 'last_24h' | 'last_7d' | 'last_30d' | 'custom' = 'last_7d'): Promise<AuditLogStats> => {
  try {
    const response = await axiosInstance.get<AuditLogStats>('/audit-logs/stats', { 
      params: { time_range: timeRange } 
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching audit log stats:', error);
    throw error;
  }
};

// Get all customers for integration purposes
export const getCustomersForIntegration = async (): Promise<Tenant[]> => {
  try {
    // Use the regular getTenants function with a filter for active customers
    const tenants = await getTenants({ 
      status: 'active',
      // We're not filtering by level anymore to include L0, L2, and L3
    });
    
    // Sort customers by level (L0, L2, L3) and then by name
    return tenants.sort((a, b) => {
      // First sort by level
      const levelOrder: Record<string, number> = { 'L0': 0, 'L2': 1, 'L3': 2 };
      const levelA = levelOrder[a.level] || 999;
      const levelB = levelOrder[b.level] || 999;
      
      if (levelA !== levelB) {
        return levelA - levelB;
      }
      
      // Then sort by name
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    console.error('Error fetching customers for integration:', error);
    return [];
  }
};
